import { renderTemplateContent } from "../contentRenderer";
import { mergeTemplateDesign, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { webSystemTemplateDesignDefaults } from "../webSystemTemplateDesign";
import { KineticMobileMenu } from "./KineticMobileMenu";
import type {
    ResolvedTemplateDesign,
    SiteTemplateDefinition,
    TemplateLinkComponent,
    TemplateRenderProps,
} from "../types";

function KineticStyles() {
    return (
        <style>{`
            [data-web-template="kinetic-club"] .site-builder-section {
                padding-inline: 1.25rem;
                position: relative;
            }

            [data-web-template="kinetic-club"] .site-builder-grid {
                display: grid;
                gap: clamp(1.25rem, 2.8vw, 2.65rem);
                margin-inline: auto;
                width: min(100%, var(--site-container));
            }

            [data-web-template="kinetic-club"] .site-builder-grid:not(.site-builder-grid-placed) {
                grid-template-columns: repeat(12, minmax(0, 1fr));
            }

            [data-web-template="kinetic-club"] .site-builder-grid:not(.site-builder-grid-placed) > .site-builder-grid-item {
                grid-column: 1 / -1;
            }

            [data-web-template="kinetic-club"] .site-builder-column {
                align-content: start;
                display: grid;
                gap: 1.15rem;
                min-width: 0;
            }

            [data-web-template="kinetic-club"] .site-builder-column-inline {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
            }

            [data-web-template="kinetic-club"] .site-builder-navigation {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
                gap: clamp(1rem, 2.2vw, 2.1rem);
                justify-content: center;
            }

            [data-web-template="kinetic-club"] .site-builder-navigation-item {
                display: inline-flex;
            }

            [data-web-template="kinetic-club"] .site-builder-navigation-link,
            [data-web-template="kinetic-club"] [data-document-role="headerNavigation"] .site-builder-text {
                display: inline-flex;
                position: relative;
                width: auto !important;
            }

            [data-web-template="kinetic-club"] [data-web-region="header"] {
                position: sticky;
                top: 0;
                z-index: 40;
            }

            [data-web-template="kinetic-club"] [data-web-region="header"] > .site-builder-grid {
                align-items: center;
            }

            [data-web-template="kinetic-club"] [data-document-role="headerActions"] {
                flex-wrap: nowrap;
                justify-content: flex-end;
            }

            [data-web-template="kinetic-club"] [data-document-role="headerCta"],
            [data-web-template="kinetic-club"] [data-document-role="localeActions"] {
                flex: 0 0 auto;
            }

            [data-web-template="kinetic-club"] [data-document-role="heroCopy"]:has(> [data-document-role="heroHeading"]) {
                column-gap: 12px;
                display: grid;
                grid-template-columns: repeat(12, minmax(0, 1fr));
                row-gap: 24px;
                width: 100%;
            }

            [data-web-template="kinetic-club"] [data-document-role="heroEyebrow"],
            [data-web-template="kinetic-club"] [data-document-role="heroHeading"],
            [data-web-template="kinetic-club"] [data-document-role="heroDescription"],
            [data-web-template="kinetic-club"] [data-document-role="heroProof"] {
                grid-column: 1 / -1;
            }

            [data-web-template="kinetic-club"] [data-document-role="heroPrimaryAction"] {
                grid-column: 1 / span 6;
            }

            [data-web-template="kinetic-club"] [data-document-role="heroSecondaryAction"] {
                grid-column: 7 / span 6;
            }

            [data-web-template="kinetic-club"] [data-document-role="heroVisual"],
            [data-web-template="kinetic-club"] [data-document-role="editorialVisual"] {
                position: relative;
            }

            [data-web-template="kinetic-club"] [data-document-role="editorialVisual"] .site-builder-image {
                min-height: 100%;
            }

            [data-web-template="kinetic-club"] [data-document-role="heroFloatingBadge"] {
                bottom: 24px;
                left: -22px;
                position: absolute;
                transform: rotate(-3deg);
                z-index: 2;
            }

            [data-web-template="kinetic-club"] [data-web-region="pulseMetrics"] > .site-builder-grid {
                gap: 1px;
            }

            [data-web-template="kinetic-club"] [data-web-region="footer"] .site-builder-navigation {
                align-items: flex-start;
                flex-direction: column;
                gap: .7rem;
            }

            [data-web-template="kinetic-club"] {
                --kinetic-black: #0b0d0b;
                --kinetic-chalk: #f3f1e8;
                scroll-behavior: smooth;
                scroll-padding-top: 76px;
            }

            [data-web-template="kinetic-club"] [data-web-region="header"] {
                background: linear-gradient(180deg, rgba(4, 6, 4, .78), transparent) !important;
                border-color: transparent !important;
                left: 0;
                min-height: 110px;
                padding-inline: clamp(1rem, 3vw, 2.5rem);
                position: fixed !important;
                right: 0;
                transition: background 280ms ease, border-color 280ms ease, box-shadow 280ms ease, min-height 280ms ease;
            }

            [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-header-scrolled] {
                backdrop-filter: blur(18px) saturate(120%);
                background: rgba(11, 13, 11, .94) !important;
                border-color: var(--site-border) !important;
                box-shadow: 0 16px 52px rgba(0, 0, 0, .34);
                min-height: 70px;
            }

            [data-web-template="kinetic-club"] [data-web-region="hero"] {
                background: var(--kinetic-black) !important;
                min-height: 100svh;
                overflow: hidden;
                padding: 0;
            }

            [data-web-template="kinetic-club"] [data-web-region="hero"]::after {
                background:
                    linear-gradient(90deg, rgba(4, 6, 4, .92) 0%, rgba(4, 6, 4, .62) 48%, rgba(4, 6, 4, .08) 82%),
                    linear-gradient(0deg, rgba(4, 6, 4, .62), transparent 54%);
                content: "";
                inset: 0;
                pointer-events: none;
                position: absolute;
                z-index: 1;
            }

            [data-web-template="kinetic-club"] [data-web-region="hero"]::before {
                background: var(--site-accent);
                bottom: 0;
                clip-path: polygon(0 78%, 100% 0, 100% 100%, 0 100%);
                content: "";
                height: clamp(6px, 2.4vw, 34px);
                left: 0;
                position: absolute;
                right: 0;
                z-index: 3;
            }

            [data-web-template="kinetic-club"] [data-web-region="hero"] > .site-builder-grid {
                min-height: 100svh;
                position: relative;
                width: 100%;
            }

            [data-web-template="kinetic-club"] [data-web-region="hero"] [data-document-role="heroVisual"] {
                grid-column: 1 / -1 !important;
                grid-row: 1 / -1 !important;
                inset: 0;
                min-height: 100svh;
                position: absolute;
                width: 100%;
                z-index: 0;
            }

            [data-web-template="kinetic-club"] [data-web-region="hero"] [data-document-role="heroVisual"] .site-builder-image {
                filter: grayscale(.38) contrast(1.1) brightness(.78);
                height: 100svh !important;
                min-height: 100svh !important;
                object-position: 62% center !important;
                transform: scale(1.055);
                transition: filter 900ms ease, transform 7.6s cubic-bezier(.2,.72,.2,1);
            }

            [data-web-template="kinetic-club"][data-kinetic-runtime-ready] [data-web-region="hero"] [data-document-role="heroVisual"] .site-builder-image {
                filter: grayscale(.18) contrast(1.08) brightness(.8);
                transform: scale(1);
            }

            [data-web-template="kinetic-club"] [data-web-region="hero"] [data-document-role="heroCopy"] {
                align-content: end;
                grid-column: 1 / span 8 !important;
                grid-row: 1 / -1 !important;
                min-height: 100svh;
                padding: clamp(9rem, 18vh, 13rem) clamp(1rem, 3vw, 3rem) clamp(4.5rem, 9vh, 7rem);
                position: relative;
                z-index: 2;
            }

            [data-web-template="kinetic-club"] [data-web-region="hero"] [data-document-role="heroHeading"] {
                font-size: clamp(4.2rem, 10.6vw, 10rem) !important;
                max-width: 9ch !important;
                text-shadow: 0 5px 34px rgba(0, 0, 0, .48);
            }

            [data-web-template="kinetic-club"] [data-web-region="hero"] [data-document-role="heroDescription"],
            [data-web-template="kinetic-club"] [data-web-region="hero"] [data-document-role="heroProof"] {
                color: var(--kinetic-chalk) !important;
                text-shadow: 0 4px 24px rgba(0, 0, 0, .62);
            }

            [data-web-template="kinetic-club"] [data-web-region="hero"] [data-document-role="heroFloatingBadge"] {
                bottom: clamp(2rem, 5vw, 4rem);
                left: auto;
                right: clamp(1.25rem, 4vw, 4rem);
                z-index: 4;
            }

            [data-web-template="kinetic-club"] [data-web-region="pulseMetrics"] {
                position: relative;
                z-index: 4;
            }

            [data-web-template="kinetic-club"] [data-web-region="pulseMetrics"] .site-builder-card {
                border: 0 !important;
            }

            [data-web-template="kinetic-club"] .site-builder-form :where(input, textarea)::placeholder {
                color: var(--site-muted);
                opacity: 1;
            }

            [data-web-template="kinetic-club"] [data-document-role="editorialVisual"] {
                overflow: hidden;
            }

            [data-web-template="kinetic-club"] [data-document-role="editorialVisual"] .site-builder-image,
            [data-web-template="kinetic-club"] [data-web-region="communityGallery"] .site-builder-image {
                filter: grayscale(.72) contrast(1.08);
                transition: filter 650ms ease, transform 900ms cubic-bezier(.2,.72,.2,1);
            }

            [data-web-template="kinetic-club"] :where(
                [data-web-region="trialCta"],
                [data-web-region="programBooking"],
                [data-web-region="coachCta"],
                [data-web-region="membershipCta"]
            ) {
                overflow: hidden;
            }

            [data-web-template="kinetic-club"] :where(
                [data-web-region="trialCta"],
                [data-web-region="programBooking"],
                [data-web-region="coachCta"],
                [data-web-region="membershipCta"]
            )::after {
                border: clamp(16px, 2.4vw, 38px) solid rgba(200, 255, 61, .1);
                border-radius: 50%;
                content: "";
                height: min(48vw, 620px);
                pointer-events: none;
                position: absolute;
                right: -15vw;
                top: -62%;
                width: min(48vw, 620px);
            }

            [data-web-template="kinetic-club"]:not([data-web-template-editor="true"])[data-kinetic-runtime-ready] [data-kinetic-reveal] {
                opacity: 0;
                transform: translate3d(0, 26px, 0);
                transition: opacity 540ms ease, transform 700ms cubic-bezier(.2,.72,.2,1);
            }

            [data-web-template="kinetic-club"]:not([data-web-template-editor="true"])[data-kinetic-runtime-ready] [data-kinetic-reveal][data-kinetic-revealed] {
                opacity: 1;
                transform: translate3d(0, 0, 0);
            }

            @media (hover: hover) and (pointer: fine) {
                [data-web-template="kinetic-club"] [data-document-role="editorialVisual"]:hover .site-builder-image,
                [data-web-template="kinetic-club"] [data-web-region="communityGallery"] .site-builder-image:hover {
                    filter: grayscale(0) contrast(1.04);
                    transform: scale(1.028);
                }

                [data-web-template="kinetic-club"] .site-builder-card:hover {
                    border-color: color-mix(in srgb, var(--site-accent) 54%, var(--site-border));
                    box-shadow: 0 24px 70px rgba(0, 0, 0, .28);
                    transform: translateY(-5px);
                }
            }

            [data-web-template="kinetic-club"] .kinetic-mobile-menu-toggle {
                display: none;
            }

            @container (max-width: 900px) {
                [data-web-template="kinetic-club"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="kinetic-club"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] > .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="kinetic-club"] [data-web-region="pulseMetrics"] > .site-builder-grid {
                    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                }

                [data-web-template="kinetic-club"] [data-web-region="pulseMetrics"] > .site-builder-grid > .site-builder-grid-item {
                    grid-column: auto !important;
                    grid-row: auto !important;
                }

                [data-web-template="kinetic-club"] [data-document-role="heroCopy"]:has(> [data-document-role="heroHeading"]) {
                    grid-template-columns: minmax(0, 1fr);
                }

                [data-web-template="kinetic-club"] [data-document-role="heroEyebrow"],
                [data-web-template="kinetic-club"] [data-document-role="heroHeading"],
                [data-web-template="kinetic-club"] [data-document-role="heroDescription"],
                [data-web-template="kinetic-club"] [data-document-role="heroPrimaryAction"],
                [data-web-template="kinetic-club"] [data-document-role="heroSecondaryAction"],
                [data-web-template="kinetic-club"] [data-document-role="heroProof"] {
                    grid-column: 1;
                }

                [data-web-template="kinetic-club"] [data-document-role="heroFloatingBadge"] {
                    bottom: 1.25rem;
                    left: auto;
                    right: 1rem;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"] {
                    min-height: 72px;
                    padding-inline: 1rem;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] {
                    background: rgba(11, 13, 11, .98) !important;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    display: block;
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="kinetic-club"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: none;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"] .kinetic-mobile-menu-toggle {
                    align-items: center;
                    background: transparent;
                    border: 0;
                    cursor: pointer;
                    display: grid;
                    gap: 8px;
                    height: 44px;
                    justify-self: end;
                    padding: 10px;
                    place-content: center;
                    position: absolute;
                    right: 1rem;
                    top: 14px;
                    width: 44px;
                    z-index: 3;
                }

                [data-web-template="kinetic-club"] .kinetic-mobile-menu-toggle > span {
                    background: var(--site-accent);
                    display: block;
                    height: 3px;
                    transition: transform 160ms ease;
                    width: 25px;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] {
                    bottom: 0;
                    left: 0;
                    overflow-y: auto;
                    position: fixed;
                    right: 0;
                    top: 0;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] > .site-builder-grid {
                    align-content: start;
                    min-height: 100%;
                    padding-bottom: 2rem;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: block;
                    grid-column: 1 / -1 !important;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    grid-row: 2 !important;
                    margin-top: clamp(3rem, 10vh, 6rem);
                }

                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    grid-row: 3 !important;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] [data-document-role="headerNavigation"],
                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] [data-document-role="headerActions"] {
                    align-items: stretch;
                    flex-direction: column;
                    gap: 0 !important;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] {
                    border-bottom: 1px solid var(--site-border);
                    padding-block: 1rem;
                    width: 100%;
                }

                [data-web-template="kinetic-club"] [data-web-region="hero"] [data-document-role="heroCopy"] {
                    grid-column: 1 !important;
                    padding: 8.75rem 1rem 5rem;
                }

                [data-web-template="kinetic-club"] [data-web-region="hero"] [data-document-role="heroHeading"] {
                    font-size: clamp(3.6rem, 18vw, 6.4rem) !important;
                }

                [data-web-template="kinetic-club"] [data-web-region="hero"]::after {
                    background: linear-gradient(0deg, rgba(4, 6, 4, .94) 8%, rgba(4, 6, 4, .42) 70%, rgba(4, 6, 4, .48));
                }

                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text {
                    display: block;
                    font-family: var(--site-heading-font) !important;
                    font-size: clamp(2rem, 9vw, 3.2rem) !important;
                    font-weight: 900 !important;
                    width: 100% !important;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] [data-document-role="headerActions"] {
                    gap: .75rem !important;
                    margin-top: 1rem;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] [data-document-role="headerActions"] > *,
                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] [data-document-role="headerCta"] {
                    width: 100% !important;
                }

                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] .kinetic-mobile-menu-toggle > span:first-child {
                    transform: translateY(5.5px) rotate(45deg);
                }

                [data-web-template="kinetic-club"] [data-web-region="header"][data-kinetic-mobile-menu-open] .kinetic-mobile-menu-toggle > span:last-child {
                    transform: translateY(-5.5px) rotate(-45deg);
                }
            }

            @container (max-width: 640px) {
                [data-web-template="kinetic-club"] .site-builder-section {
                    padding-inline: 1rem;
                }

                [data-web-template="kinetic-club"] [data-document-role="heroHeading"].site-builder-text-h1,
                [data-web-template="kinetic-club"] [data-document-role="heroHeading"] .site-builder-text-h1,
                [data-web-template="kinetic-club"] [data-web-region="coachesHero"] .site-builder-text-h1 {
                    font-size: clamp(46px, 13vw, 60px) !important;
                }

                [data-web-template="kinetic-club"] [data-web-region="communityGallery"] .site-builder-text-h2 {
                    font-size: clamp(35px, 9.5vw, 42px) !important;
                }

                [data-web-template="kinetic-club"] [data-web-region="pulseMetrics"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="kinetic-club"] [data-document-role="heroPrimaryAction"],
                [data-web-template="kinetic-club"] [data-document-role="heroSecondaryAction"] {
                    width: 100% !important;
                }

                [data-web-template="kinetic-club"] .site-builder-column {
                    grid-template-columns: minmax(0, 1fr) !important;
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="kinetic-club"] *,
                [data-web-template="kinetic-club"] *::before,
                [data-web-template="kinetic-club"] *::after {
                    animation-duration: .01ms !important;
                    animation-iteration-count: 1 !important;
                    scroll-behavior: auto !important;
                    transition-duration: .01ms !important;
                }

                [data-web-template="kinetic-club"] [data-kinetic-reveal] {
                    opacity: 1 !important;
                    transform: none !important;
                }
            }
        `}</style>
    );
}

function KineticFallbackPage({
    props,
    design,
    LinkComponent,
}: {
    props: TemplateRenderProps;
    design: ResolvedTemplateDesign;
    LinkComponent: TemplateLinkComponent;
}) {
    const page = props.page;

    if (!page) {
        return null;
    }

    return (
        <main
            className="px-5 py-20 sm:px-6"
            data-builder-document-region="page"
        >
            <article className="mx-auto max-w-[900px] leading-8 text-[var(--site-muted)] [&_a]:text-[var(--site-accent)] [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-5 [&_h1]:mb-7 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-6xl [&_h1]:font-black [&_h1]:uppercase [&_h1]:leading-[.9] [&_h1]:text-[var(--site-text)] [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-4xl [&_h2]:font-black [&_h2]:uppercase [&_h2]:text-[var(--site-text)] [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-5 [&_strong]:text-[var(--site-text)] [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6">
                <h1>{page.title}</h1>
                {renderTemplateContent(
                    page,
                    design,
                    LinkComponent,
                    props.leadForm,
                    props.readOnly,
                )}
            </article>
        </main>
    );
}

export const kineticClubTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "kinetic-club",
        name: "Fitness centrum",
        type: "website",
        category: "fitness",
        description: "Výrazná editoriální šablona pro moderní fitness kluby, funkční studia a malé tréninkové komunity.",
        previewImage: "/templates/kinetic-club/preview.jpg",
        defaults: webSystemTemplateDesignDefaults["kinetic-club"],
        pageTypes: {
            home: {
                label: "Fitness homepage",
                slots: ["richContent"],
            },
            content: {
                label: "Fitness content page",
                slots: ["richContent"],
            },
        },
        editable: {
            colors: true,
            typography: true,
            radius: true,
            header: ["builder"],
            footer: ["builder"],
            buttons: ["solid", "outline"],
        },
    },
    render: (props: TemplateRenderProps) => {
        const design = mergeTemplateDesign(
            kineticClubTemplate.manifest.defaults,
            props.designOverride ?? props.site?.templateDesign,
        );
        const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;

        return (
            <div
                data-web-template="kinetic-club"
                data-web-template-editor={props.editorMode ? "true" : undefined}
                className="min-h-screen overflow-x-hidden bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
                style={{
                    ...templateDesignToCssVars(design),
                    containerType: "inline-size",
                }}
            >
                <KineticStyles />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="header"
                >
                    <KineticMobileMenu
                        languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                    />
                </WebGlobalBuilderRegion>
                <WebBuilderPageRegion
                    design={design}
                    fallback={(
                        <KineticFallbackPage
                            props={props}
                            design={design}
                            LinkComponent={LinkComponent}
                        />
                    )}
                    LinkComponent={LinkComponent}
                    props={props}
                />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="footer"
                />
            </div>
        );
    },
};
